// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

import './styles/main.css';
import { log, mountEventLog, setOpen as setLogOpen, isOpen as isLogOpen } from './eventlog.ts';
import { anyOpen, closeTop, createModal, mountModalKeys, openModal } from './modal.ts';
import { mountGlossary, term } from './glossary.ts';
import { buildReport, contaminationCsv, download, tooShortCsv } from './exports.ts';
import { percent, points } from './core/stats.ts';
import type { RunOptions, RunReport, Side, SniffReport, WorkerRequest, WorkerResponse } from './types.ts';

const SETTINGS_KEY = 'evalspill:settings:v1';

interface Settings {
  stripAffixes: boolean;
  boilerplateFilter: boolean;
}

interface FileSlot {
  file: File | null;
  report: SniffReport | null;
  fields: string[];
}

interface State {
  train: FileSlot;
  eval: FileSlot;
  scoreField: string | null;
  ngramOverride: number | null;
  settings: Settings;
  phase: 'idle' | 'sniffing' | 'ready' | 'running' | 'done';
  progress: { phase: string; done: number; total: number; mbps: number; etaSeconds: number } | null;
  report: RunReport | null;
  error: string | null;
  dropped: Set<number>;
  shown: number;
}

const state: State = {
  train: { file: null, report: null, fields: [] },
  eval: { file: null, report: null, fields: [] },
  scoreField: null,
  ngramOverride: null,
  settings: loadSettings(),
  phase: 'idle',
  progress: null,
  report: null,
  error: null,
  dropped: new Set(),
  shown: 50,
};

function loadSettings(): Settings {
  const fallback: Settings = { stripAffixes: true, boilerplateFilter: true };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      stripAffixes: typeof parsed.stripAffixes === 'boolean' ? parsed.stripAffixes : true,
      boilerplateFilter: typeof parsed.boilerplateFilter === 'boolean' ? parsed.boilerplateFilter : true,
    };
  } catch {
    return fallback;
  }
}

function saveSettings(): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.settings));
  } catch {
    /* private mode, a full quota — the tool works without remembering. */
  }
}

/* ── worker ───────────────────────────────────────────────────────────────── */

const worker = new Worker(new URL('./workers/scan.worker.ts', import.meta.url), { type: 'module' });

function send(request: WorkerRequest): void {
  worker.postMessage(request);
}

let pendingExport: ((bytes: Uint8Array, kept: number, dropped: number) => void) | null = null;

worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
  const message = event.data;
  if (message.type === 'sniffed') {
    const slot = state[message.report.side];
    slot.report = message.report;
    slot.fields = [...message.report.suggestedText];
    if (message.report.side === 'eval') state.scoreField = message.report.suggestedScore;
    state.phase = state.train.report && state.eval.report ? 'ready' : 'sniffing';
    log(
      'good',
      `${message.report.side === 'train' ? 'Training' : 'Eval'} file read: ${message.report.name} — ${message.report.format}${
        message.report.gzipped ? ' (gzip)' : ''
      }, ${message.report.sampledRecords} records sampled, ${message.report.fields.length} fields found`,
    );
    if (message.report.nearConstant.length) {
      log(
        'warn',
        `Excluded by default as near-constant: ${message.report.nearConstant.join(', ')} — identical in more than half the rows`,
      );
    }
    render();
    return;
  }
  if (message.type === 'progress') {
    state.progress = message;
    renderProgressOnly();
    return;
  }
  if (message.type === 'done') {
    state.report = message.report;
    state.phase = 'done';
    state.progress = null;
    state.dropped = new Set(message.report.flagged.map((row) => row.row));
    state.shown = 50;
    const share = message.report.evalScorable ? message.report.flaggedCount / message.report.evalScorable : 0;
    log(
      'info',
      `Scanned ${message.report.trainScanned.toLocaleString()} training records in ${(message.report.elapsedMs / 1000).toFixed(1)}s`,
    );
    log(
      message.report.flaggedCount > 0 ? 'warn' : 'good',
      `${message.report.flaggedCount.toLocaleString()} of ${message.report.evalScorable.toLocaleString()} scorable eval rows share a ${message.report.ngramSize}-word run with training (${percent(share)})`,
    );
    announce(headlineText(message.report));
    render();
    return;
  }
  if (message.type === 'exported') {
    pendingExport?.(message.bytes, message.kept, message.dropped);
    pendingExport = null;
    return;
  }
  state.error = message.message;
  state.phase = state.report ? 'done' : 'ready';
  state.progress = null;
  log('bad', message.message);
  render();
});

/* ── announcements ────────────────────────────────────────────────────────── */

function announce(text: string): void {
  // The region lives OUTSIDE #app, because render() begins with
  // replaceChildren() and a live region created inside it is destroyed by the
  // very mechanism that makes one necessary.
  const region = document.getElementById('announce');
  if (region) region.textContent = text;
}

/* ── small builders ───────────────────────────────────────────────────────── */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(label: string, className: string, onClick: () => void, focusKey?: string): HTMLButtonElement {
  const node = el('button', className, label);
  node.type = 'button';
  if (focusKey) node.dataset.focusKey = focusKey;
  node.addEventListener('click', onClick);
  return node;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/* ── ingestion ────────────────────────────────────────────────────────────── */

function accept(side: Side, file: File): void {
  state[side].file = file;
  state[side].report = null;
  state.report = null;
  state.error = null;
  state.phase = 'sniffing';
  log('info', `Reading ${side === 'train' ? 'training' : 'eval'} file: ${file.name} (${formatBytes(file.size)})`);
  send({ type: 'sniff', side, file });
  render();
}

function dropzone(side: Side): HTMLElement {
  const slot = state[side];
  const zone = el('div', 'dropzone');
  zone.tabIndex = 0;
  zone.setAttribute('role', 'button');
  zone.dataset.focusKey = `dropzone:${side}`;
  zone.setAttribute(
    'aria-label',
    side === 'train' ? 'Choose the training file' : 'Choose the eval file',
  );

  const title = el('p', 'dz-title', side === 'train' ? 'Training file' : 'Eval file');
  const hint = el(
    'p',
    'dz-hint',
    slot.file
      ? `${slot.file.name} · ${formatBytes(slot.file.size)}`
      : side === 'train'
        ? 'The JSONL, CSV or text you fine-tuned on. .gz is fine. Drag it here.'
        : 'The eval or test set you scored. Drag it here.',
  );
  zone.append(title, hint);

  const input = el('input');
  input.type = 'file';
  input.accept = '.jsonl,.ndjson,.json,.csv,.tsv,.txt,.md,.gz';
  input.hidden = true;
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) accept(side, file);
  });
  zone.appendChild(input);

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      input.click();
    }
  });
  zone.addEventListener('dragover', (ev) => {
    ev.preventDefault();
    zone.classList.add('over');
  });
  zone.addEventListener('dragleave', () => zone.classList.remove('over'));
  zone.addEventListener('drop', (ev) => {
    ev.preventDefault();
    zone.classList.remove('over');
    const file = ev.dataTransfer?.files?.[0];
    if (file) accept(side, file);
  });

  return zone;
}

async function loadSample(): Promise<void> {
  try {
    log('info', 'Loading the sample pair (Northgate Logistics, self-authored)');
    const [train, evalFile] = await Promise.all([
      fetch('/samples/train.jsonl').then((r) => r.blob()),
      fetch('/samples/eval.jsonl').then((r) => r.blob()),
    ]);
    // Through the SAME ingestion path as a real drop — never a second code path,
    // or the demo stops proving anything about the tool.
    accept('train', new File([train], 'train.jsonl', { type: 'application/x-ndjson' }));
    accept('eval', new File([evalFile], 'eval.jsonl', { type: 'application/x-ndjson' }));
  } catch (error) {
    state.error = `The sample could not be loaded: ${error instanceof Error ? error.message : String(error)}`;
    render();
  }
}

/* ── field picking ────────────────────────────────────────────────────────── */

function fieldPicker(side: Side): HTMLElement {
  const slot = state[side];
  const box = el('div', 'field-box');
  if (!slot.report) return box;

  const label = el(
    'p',
    'field-label',
    side === 'train' ? 'Compare on these training fields' : 'Compare on these eval fields',
  );
  box.appendChild(label);

  const chips = el('div', 'chips');
  const excluded = new Set(slot.report.nearConstant);
  const candidates = slot.report.fields.filter((field) => field.meanChars >= 2).slice(0, 14);

  for (const field of candidates) {
    const selected = slot.fields.includes(field.path);
    const chip = el('button', `chip${selected ? ' on' : ''}${excluded.has(field.path) ? ' excluded' : ''}`);
    chip.type = 'button';
    chip.dataset.focusKey = `chip:${side}:${field.path}`;
    chip.setAttribute('aria-pressed', String(selected));

    const name = el('span', 'chip-name', field.path);
    const meta = el(
      'span',
      'chip-meta',
      `${Math.round(field.meanChars)} chars · ${Math.round(field.fillRate * 100)}% filled${
        excluded.has(field.path) ? ' · same on most rows' : ''
      }`,
    );
    chip.append(name, meta);
    chip.title = field.samples.join('\n');

    chip.addEventListener('click', () => {
      const index = slot.fields.indexOf(field.path);
      if (index >= 0) slot.fields.splice(index, 1);
      else slot.fields.push(field.path);
      state.report = null;
      render();
    });
    chips.appendChild(chip);
  }
  box.appendChild(chips);

  if (excluded.size > 0) {
    box.appendChild(
      el(
        'p',
        'field-note',
        `${[...excluded].join(', ')} ${excluded.size === 1 ? 'is' : 'are'} identical on more than half the rows and ${
          excluded.size === 1 ? 'is' : 'are'
        } left out by default. Comparing on a field every row shares reports near-total overlap between two perfectly clean files.`,
      ),
    );
  }

  return box;
}

/* ── controls ─────────────────────────────────────────────────────────────── */

function checkbox(label: string, checked: boolean, onChange: (value: boolean) => void, key: string): HTMLElement {
  const wrap = el('label', 'check');
  const input = el('input');
  input.type = 'checkbox';
  input.checked = checked;
  input.dataset.focusKey = key;
  input.addEventListener('change', () => onChange(input.checked));
  wrap.append(input, document.createTextNode(` ${label}`));
  return wrap;
}

function controls(): HTMLElement {
  const box = el('div', 'controls');

  box.appendChild(
    checkbox(
      'Ignore word runs that appear in many training records',
      state.settings.boilerplateFilter,
      (value) => {
        state.settings.boilerplateFilter = value;
        state.report = null;
        saveSettings();
        render();
      },
      'check:boilerplate',
    ),
  );
  box.appendChild(
    checkbox(
      'Strip a prefix or suffix shared by nearly every row',
      state.settings.stripAffixes,
      (value) => {
        state.settings.stripAffixes = value;
        state.report = null;
        saveSettings();
        render();
      },
      'check:affix',
    ),
  );

  const note = el('p', 'field-note');
  note.appendChild(document.createTextNode('With the first on, the training file is read twice: once to find which runs are '));
  note.appendChild(term('boilerplate', 'boilerplate filter'));
  note.appendChild(document.createTextNode(', once to match. Turning it off halves the time and reports template text as overlap.'));
  box.appendChild(note);

  if (state.eval.report?.suggestedScore) {
    box.appendChild(
      el(
        'p',
        'field-note',
        `A per-row score was found in "${state.eval.report.suggestedScore}". It will be used to bound what the overlap can do to your headline number.`,
      ),
    );
  }

  return box;
}

/* ── the run ──────────────────────────────────────────────────────────────── */

function currentOptions(): RunOptions {
  return {
    trainFields: state.train.fields,
    evalFields: state.eval.fields,
    scoreField: state.scoreField,
    ngramOverride: state.ngramOverride,
    stripAffixes: state.settings.stripAffixes,
    boilerplateFilter: state.settings.boilerplateFilter,
    trainSampleLimit: 0,
  };
}

function start(): void {
  if (!state.train.file || !state.eval.file) return;
  state.phase = 'running';
  state.error = null;
  state.report = null;
  state.progress = { phase: 'Starting', done: 0, total: state.train.file.size, mbps: 0, etaSeconds: 0 };
  log('info', `Comparing on ${state.eval.fields.join(' + ') || 'the whole record'} (eval) against ${state.train.fields.join(' + ') || 'the whole record'} (training)`);
  announce('Scanning.');
  send({ type: 'run', train: state.train.file, evalFile: state.eval.file, options: currentOptions() });
  render();
}

let progressNode: HTMLElement | null = null;

function progressBox(): HTMLElement {
  const box = el('div', 'progress-box');
  progressNode = box;
  paintProgress();
  return box;
}

function paintProgress(): void {
  if (!progressNode) return;
  const progress = state.progress;
  progressNode.replaceChildren();
  if (!progress) return;

  const track = el('div', 'progress-track');
  const fill = el('div', 'progress-fill');
  fill.style.width = `${progress.total ? Math.min(100, (progress.done / progress.total) * 100) : 0}%`;
  track.appendChild(fill);

  const line = el(
    'p',
    'progress-line',
    `${progress.phase} — ${formatBytes(progress.done)} of ${formatBytes(progress.total)} · ${progress.mbps.toFixed(
      0,
    )} MB/s${progress.etaSeconds > 1 ? ` · about ${Math.ceil(progress.etaSeconds)}s left` : ''}`,
  );

  progressNode.append(track, line);
}

function renderProgressOnly(): void {
  paintProgress();
}

/* ── results ──────────────────────────────────────────────────────────────── */

function headlineText(report: RunReport): string {
  const share = report.evalScorable ? report.flaggedCount / report.evalScorable : 0;
  if (report.flaggedCount === 0) {
    return `No eval row shares a run of ${report.ngramSize} words with the training file.`;
  }
  return `${report.flaggedCount.toLocaleString()} of ${report.evalScorable.toLocaleString()} scorable eval rows contain a ${report.ngramSize}-word passage that also appears in your training file — ${percent(share)}.`;
}

function results(report: RunReport): HTMLElement {
  const box = el('section', 'results');

  const headline = el('div', 'headline');
  const figure = el('p', `headline-figure${report.flaggedCount > 0 ? ' bad' : ' good'}`, headlineText(report));
  headline.appendChild(figure);

  const sub = el('p', 'headline-sub');
  sub.appendChild(document.createTextNode(`N was set to ${report.ngramSize} words `));
  sub.appendChild(
    term(
      report.ngramSource === 'gpt3-rule' ? "from your eval file's own row lengths" : 'by hand',
      'n-gram',
    ),
  );
  sub.appendChild(document.createTextNode('. '));
  if (report.evalTooShort > 0) {
    sub.appendChild(
      document.createTextNode(
        `${report.evalTooShort.toLocaleString()} eval rows are shorter than that and were not checked — unmeasured, not clean.`,
      ),
    );
  }
  headline.appendChild(sub);
  box.appendChild(headline);

  if (report.comparison) {
    const c = report.comparison;
    const scoreBox = el('div', 'score-box');
    scoreBox.appendChild(el('p', 'score-figure', `${percent(c.overall)} → ${percent(c.bound.low)} … ${percent(c.bound.high)}`));
    scoreBox.appendChild(
      el(
        'p',
        'score-sub',
        `Your eval scores ${percent(c.overall)}. Removing the overlapping rows moves that to somewhere in this range — at most ${points(
          c.bound.maxDrop,
        )} points down.`,
      ),
    );
    if (c.withinNoise) {
      scoreBox.appendChild(
        el(
          'p',
          'score-note',
          `Measured directly: ${percent(c.cleanMean)} (${percent(c.cleanInterval.low)}–${percent(
            c.cleanInterval.high,
          )}) on the clean rows against ${percent(c.flaggedMean)} (${percent(c.flaggedInterval.low)}–${percent(
            c.flaggedInterval.high,
          )}) on the overlapping ones. Those intervals overlap, so this comparison does not separate them.`,
        ),
      );
    } else {
      scoreBox.appendChild(
        el(
          'p',
          'score-note',
          `Measured directly, the clean subset scores ${percent(c.cleanMean)} (${percent(
            c.cleanInterval.low,
          )}–${percent(c.cleanInterval.high)}). That is a re-measurement on a smaller, different set of questions — not a corrected score.`,
        ),
      );
    }
    box.appendChild(scoreBox);
  } else if (report.scoreRefusedReason) {
    box.appendChild(el('p', 'field-note warn', report.scoreRefusedReason));
  }

  // The facts that stop a reader over-reading the headline, before the table.
  const facts = el('ul', 'facts');
  const factLines: string[] = [];
  if (report.documentFrequencyCap > 0) {
    factLines.push(
      `${report.boilerplateNgrams.toLocaleString()} word runs were ignored for appearing in more than ${report.documentFrequencyCap.toLocaleString()} distinct training records.`,
    );
  }
  if (report.strippedPrefix || report.strippedSuffix) {
    factLines.push(
      `A shared prefix of ${report.strippedPrefix.length} characters and suffix of ${report.strippedSuffix.length} were stripped from every row before comparing.`,
    );
  }
  if (report.evalDuplicateRows > 0 || report.trainDuplicateRows > 0) {
    factLines.push(
      `${report.trainDuplicateRows.toLocaleString()} training rows and ${report.evalDuplicateRows.toLocaleString()} eval rows are exact duplicates of another row in the same file.`,
    );
  }
  factLines.push(
    'One eval row matching four hundred training rows is one overlap, not four hundred. Every count here is per eval row.',
  );
  for (const line of factLines) {
    const item = el('li', undefined, line);
    facts.appendChild(item);
  }
  box.appendChild(facts);

  if (report.flagged.length > 0) {
    box.appendChild(el('h2', undefined, 'The overlapping rows'));
    box.appendChild(
      el(
        'p',
        'field-note',
        'The highlighted passage is the longest run of words this row shares with the training file. Read it: that is the whole check.',
      ),
    );
    box.appendChild(matchTable(report));
  }

  box.appendChild(exportsBox(report));
  return box;
}

function matchTable(report: RunReport): HTMLElement {
  const wrap = el('div', 'table-wrap');
  const table = el('table', 'match-table');

  const thead = el('thead');
  const headRow = el('tr');
  for (const label of ['Drop', 'Row', 'Longest', 'Containment', 'The eval row, with the shared passage marked']) {
    headRow.appendChild(el('th', label === 'Row' || label === 'Longest' || label === 'Containment' ? 'n' : undefined, label));
  }
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = el('tbody');
  for (const row of report.flagged.slice(0, state.shown)) {
    const tr = el('tr');

    const dropCell = el('td');
    const dropInput = el('input');
    dropInput.type = 'checkbox';
    dropInput.checked = state.dropped.has(row.row);
    dropInput.dataset.focusKey = `drop:${row.row}`;
    dropInput.setAttribute('aria-label', `Remove eval row ${row.recordNumber} from the cleaned file`);
    dropInput.addEventListener('change', () => {
      if (dropInput.checked) state.dropped.add(row.row);
      else state.dropped.delete(row.row);
      const label = document.getElementById('drop-count');
      if (label) label.textContent = String(state.dropped.size);
    });
    dropCell.appendChild(dropInput);

    const number = el('td', 'n', String(row.recordNumber));
    const longest = el('td', 'n', `${row.longestMatchWords} w`);
    const containment = el('td', 'n', percent(row.containment));

    const text = el('td', 'match-text');
    const clipped = row.text.length > 900 ? `${row.text.slice(0, 900)}…` : row.text;
    const spanStart = Math.min(row.spanStart, clipped.length);
    const spanEnd = Math.min(row.spanEnd, clipped.length);
    text.appendChild(document.createTextNode(clipped.slice(0, spanStart)));
    const mark = el('mark');
    mark.textContent = clipped.slice(spanStart, spanEnd);
    text.appendChild(mark);
    text.appendChild(document.createTextNode(clipped.slice(spanEnd)));

    if (row.example) {
      const excerpt = el('div', 'excerpt');
      excerpt.appendChild(el('span', 'excerpt-label', `in training record ${row.example.trainRecord + 1}`));
      const body = el('span');
      const ex = row.example;
      body.appendChild(document.createTextNode(ex.excerpt.slice(0, ex.spanStart)));
      const exMark = el('mark');
      exMark.textContent = ex.excerpt.slice(ex.spanStart, ex.spanEnd);
      body.appendChild(exMark);
      body.appendChild(document.createTextNode(ex.excerpt.slice(ex.spanEnd)));
      excerpt.appendChild(body);
      text.appendChild(excerpt);
    } else if (report.excerptBudgetReached) {
      text.appendChild(el('div', 'excerpt', 'No training excerpt was kept for this row — the excerpt budget was reached.'));
    }

    tr.append(dropCell, number, longest, containment, text);
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  wrap.appendChild(table);

  if (report.flagged.length > state.shown) {
    wrap.appendChild(
      button(
        `Show ${Math.min(200, report.flagged.length - state.shown)} more of ${report.flagged.length.toLocaleString()}`,
        'ghost-btn',
        () => {
          state.shown += 200;
          render();
        },
        'more',
      ),
    );
  }
  if (report.flaggedCount > report.flagged.length) {
    wrap.appendChild(
      el(
        'p',
        'field-note',
        `${report.flaggedCount.toLocaleString()} rows matched; the strongest ${report.flagged.length.toLocaleString()} are listed. The CSV carries the same set.`,
      ),
    );
  }
  return wrap;
}

function exportsBox(report: RunReport): HTMLElement {
  const box = el('div', 'exports');
  const options = currentOptions();
  const trainName = state.train.report?.name ?? 'training file';
  const evalName = state.eval.report?.name ?? 'eval file';

  const count = el('p', 'export-count');
  count.appendChild(document.createTextNode('Rows ticked for removal: '));
  const number = el('strong', undefined, String(state.dropped.size));
  number.id = 'drop-count';
  count.appendChild(number);
  box.appendChild(count);

  const row = el('div', 'export-row');
  row.appendChild(
    button('Download eval.clean.jsonl', 'primary-btn', () => {
      pendingExport = (bytes, kept, dropped) => {
        download(new Blob([bytes as BlobPart], { type: 'application/x-ndjson' }), 'eval.clean.jsonl');
        log('good', `eval.clean.jsonl written — ${kept.toLocaleString()} rows kept, ${dropped.toLocaleString()} removed`);
      };
      send({ type: 'exportEval', drop: [...state.dropped] });
    }, 'export:clean'),
  );
  row.appendChild(
    button('contamination-report.csv', 'download-btn', () => {
      download(contaminationCsv(report, options), 'contamination-report.csv');
    }, 'export:csv'),
  );
  if (report.tooShort.length > 0) {
    row.appendChild(
      button('too-short-to-score.csv', 'download-btn', () => {
        download(tooShortCsv(report, options), 'too-short-to-score.csv');
      }, 'export:short'),
    );
  }
  row.appendChild(
    button('report.html', 'download-btn', () => {
      download(buildReport(report, options, trainName, evalName), 'overlap-report.html');
    }, 'export:report'),
  );
  box.appendChild(row);

  box.appendChild(
    el(
      'p',
      'field-note',
      'eval.clean.jsonl is the original bytes of the rows you kept — same field order, same escaping, same line endings — so it is a drop-in replacement. Removing rows from the TRAINING side is not offered here: it needs another full pass over a file that may be gigabytes, and rewriting it inside a browser tab is not something to do casually.',
    ),
  );

  return box;
}

/* ── chrome ───────────────────────────────────────────────────────────────── */

let footerNode: HTMLElement | null = null;

/**
 * MEMOISED, and that is load-bearing rather than an optimisation.
 *
 * The hosted feedback widget appends its link into `.site-footer .footer-inner`
 * on DOMContentLoaded and has no way to notice that the element it was handed
 * has been replaced. render() calls app.replaceChildren(), so a footer rebuilt
 * each time loses the widget's link on the first interaction — present on first
 * paint, gone forever after. A sibling tool shipped exactly that.
 */
function footer(): HTMLElement {
  if (footerNode) return footerNode;
  const foot = el('footer', 'site-footer');
  const inner = el('div', 'footer-inner');
  const built = el('span');
  built.innerHTML =
    'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>';
  inner.appendChild(built);
  foot.appendChild(inner);
  footerNode = foot;
  return foot;
}

const modals = new Map<string, HTMLElement>();

function openNamed(id: string, title: string, templateId: string, opener: HTMLElement): void {
  let root = modals.get(id);
  if (!root) {
    root = createModal(id, title, templateId);
    modals.set(id, root);
  }
  openModal(root, opener);
}

function header(): HTMLElement {
  const bar = el('header', 'site-header');

  const brand = el('div', 'brand');
  const mark = el('span', 'brand-mark');
  mark.innerHTML =
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="1.5" y="3" width="10" height="14" rx="2" stroke="currentColor" stroke-width="1.4"/><rect x="8.5" y="3" width="10" height="14" rx="2" stroke="currentColor" stroke-width="1.4"/><path d="M9 8h2M9 11h2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
  brand.append(mark, el('span', 'brand-name', 'evalspill'));

  const nav = el('nav', 'site-nav');
  nav.appendChild(
    button('How it works', 'ghost-btn', function (this: void) {
      openNamed('modal-how', 'How it works', 'tpl-how', nav.children[0] as HTMLElement);
    }),
  );
  nav.appendChild(
    button('Privacy', 'ghost-btn', () => {
      openNamed('modal-privacy', 'Privacy', 'tpl-privacy', nav.children[1] as HTMLElement);
    }),
  );
  nav.appendChild(
    button('About', 'ghost-btn', () => {
      openNamed('modal-about', 'About evalspill', 'tpl-about', nav.children[2] as HTMLElement);
    }),
  );
  mountEventLog(nav);

  bar.append(brand, nav);
  return bar;
}

/* ── render ───────────────────────────────────────────────────────────────── */

const app = document.getElementById('app') as HTMLElement;

interface FocusMemory {
  key: string;
  start: number | null;
}

function captureFocus(): FocusMemory | null {
  const active = document.activeElement as HTMLElement | null;
  const key = active?.dataset?.focusKey;
  if (!key) return null;
  const input = active as HTMLInputElement;
  return { key, start: typeof input.selectionStart === 'number' ? input.selectionStart : null };
}

function restoreFocus(memory: FocusMemory | null): void {
  if (!memory) return;
  const target = app.querySelector(`[data-focus-key="${CSS.escape(memory.key)}"]`) as HTMLElement | null;
  if (!target) return;
  target.focus();
  if (memory.start !== null && 'setSelectionRange' in target) {
    try {
      (target as HTMLInputElement).setSelectionRange(memory.start, memory.start);
    } catch {
      /* not a text input after all */
    }
  }
}

function render(): void {
  // Focus is read BEFORE the teardown and restored after. Without this, ticking
  // a field chip drops focus to <body> with the element just operated on
  // detached, and a keyboard user re-tabs from the top of the document after
  // every single interaction — which makes choosing a field set impossible.
  const memory = captureFocus();
  app.replaceChildren();
  progressNode = null;

  app.appendChild(header());

  const main = el('main', 'main-content');

  const heading = el('h1', undefined, 'Does your eval set overlap your training data?');
  main.appendChild(heading);

  const lede = el('p', 'lede');
  lede.appendChild(
    document.createTextNode(
      'Drop the file you fine-tuned on and the eval set you scored. evalspill finds the eval rows containing a passage that also appears in training — not just identical rows, but a question quoted inside a longer document, which is what every published ',
    ),
  );
  lede.appendChild(term('decontamination', 'decontamination'));
  lede.appendChild(
    document.createTextNode(
      ' recipe actually measures. Both files are read in this tab. Neither is uploaded.',
    ),
  );
  main.appendChild(lede);

  const zones = el('div', 'zones');
  zones.append(dropzone('train'), dropzone('eval'));
  main.appendChild(zones);

  const sample = button('Try the sample pair', 'pill-btn', () => void loadSample(), 'sample');
  main.appendChild(sample);

  if (state.error) {
    const box = el('div', 'error-box');
    box.appendChild(el('p', undefined, state.error));
    box.appendChild(
      button('Try again', 'ghost-btn', () => {
        state.error = null;
        render();
      }),
    );
    main.appendChild(box);
  }

  if (state.train.report || state.eval.report) {
    const pickers = el('div', 'pickers');
    if (state.train.report) pickers.appendChild(fieldPicker('train'));
    if (state.eval.report) pickers.appendChild(fieldPicker('eval'));
    main.appendChild(pickers);
  }

  if (state.phase === 'ready' || state.phase === 'done' || state.phase === 'running') {
    main.appendChild(controls());
    const canRun =
      state.phase !== 'running' && state.train.fields.length > 0 && state.eval.fields.length > 0;
    const run = button(
      state.phase === 'running' ? 'Scanning…' : 'Find the overlap',
      'primary-btn',
      () => start(),
      'run',
    );
    run.disabled = !canRun;
    main.appendChild(run);
  }

  if (state.phase === 'running') main.appendChild(progressBox());
  if (state.report) main.appendChild(results(state.report));

  app.appendChild(main);
  app.appendChild(footer());

  restoreFocus(memory);
}

/* ── boot ─────────────────────────────────────────────────────────────────── */

mountGlossary();
mountModalKeys(() => {
  if (anyOpen()) {
    closeTop();
    return true;
  }
  if (isLogOpen()) {
    setLogOpen(false);
    return true;
  }
  return false;
});

document.addEventListener('keydown', (ev) => {
  if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey) && state.phase === 'ready') start();
});

render();
log('info', 'evalspill ready. Nothing is uploaded — you can watch this log while it runs.');
